import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Outlet, Navigate, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/theme/ThemeProvider";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Layout } from "@/components/Layout";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import About from "./pages/About.tsx";
import Services from "./pages/Services.tsx";
import Portfolio from "./pages/Portfolio.tsx";
import Technology from "./pages/Technology.tsx";
import Contact from "./pages/Contact.tsx";
import Ventures from "./pages/Ventures.tsx";
import Unsubscribe from "./pages/Unsubscribe";
import { AuthProvider } from "@/auth/AuthProvider";
import { RequireRole } from "@/auth/RequireRole";
import { OSLayout } from "@/os/OSLayout";
import { ImpersonationProvider } from "@/os/impersonation/ImpersonationProvider";
// Short link used in client-facing quote emails (e.g. /q/<id>).
// Forwards into the OS client area, which (via RequireRole) bounces the
// recipient through /os/login first and then lands them on their quote.
function QuoteShortLinkRedirect() {
  const { id } = useParams();
  return <Navigate to={`/os/client/quotes/${id}`} replace />;
}
// Smart Canvas OS — lazy so guests never download the backoffice bundle.
const OSLogin = lazy(() => import("./pages/os/Login"));
const OSRedirect = lazy(() => import("./pages/os/OSRedirect"));
const EnvSelect = lazy(() => import("./pages/os/EnvSelect"));
const Account = lazy(() => import("./pages/os/Account"));
const AdminDashboard = lazy(() => import("./pages/os/admin/AdminDashboard"));
const AdminClients = lazy(() => import("./pages/os/admin/Clients"));
const AdminProjects = lazy(() => import("./pages/os/admin/Projects"));
const AdminQuotes = lazy(() => import("./pages/os/admin/Quotes"));
const AdminQuoteDetail = lazy(() => import("./pages/os/admin/QuoteDetail"));
const QuoteWizardPage = lazy(() => import("./pages/os/admin/QuoteWizardPage"));
const AdminInvoices = lazy(() => import("./pages/os/admin/Invoices"));
const AdminUsers = lazy(() => import("./pages/os/admin/Users"));
const AdminTeam = lazy(() => import("./pages/os/admin/Team"));
const AdminStudio = lazy(() => import("./pages/os/admin/Studio"));
const AdminResources = lazy(() => import("./pages/os/admin/Resources"));
const AdminSettings = lazy(() => import("./pages/os/admin/Settings"));
const AdminMonitoring = lazy(() => import("./pages/os/admin/Monitoring"));
const OperatorDashboard = lazy(() => import("./pages/os/operator/OperatorDashboard"));
const OperatorProjects = lazy(() => import("./pages/os/operator/OperatorProjects"));
const WorkStudio = lazy(() => import("./pages/os/operator/WorkStudio"));
const ClientDashboard = lazy(() => import("./pages/os/client/ClientDashboard"));
const ClientProjects = lazy(() => import("./pages/os/client/ClientProjects"));
const ClientInvoices = lazy(() => import("./pages/os/client/ClientInvoices"));
const ClientProjectDetail = lazy(() => import("./pages/os/client/ClientProjectDetail"));
const ClientQuotes = lazy(() => import("./pages/os/client/ClientQuotes"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered "fresh" for 60s — navigating between pages
      // within this window shows cached data instantly with no refetch.
      staleTime: 60_000,
      // Keep cached data in memory for 10 minutes after the last
      // observer unmounts, so returning to a page is instant.
      gcTime: 10 * 60_000,
      // Revalidate in background when the tab regains focus, but only
      // if the data is stale — no parpadeo, just silent updates.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Don't refetch on every remount if data is still fresh.
      refetchOnMount: true,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <ImpersonationProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Smart Canvas OS — backoffice */}
                <Route path="/os/login" element={<OSLogin />} />
                <Route path="/os/select" element={<EnvSelect />} />
                <Route path="/os" element={<OSRedirect />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/q/:id" element={<QuoteShortLinkRedirect />} />

                <Route path="/os/admin" element={<RequireRole roles={["admin"]}><OSLayout role="admin" /></RequireRole>}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="clients" element={<AdminClients />} />
                  <Route path="clients/:id" element={<AdminClients />} />
                  <Route path="projects" element={<AdminProjects />} />
                  <Route path="projects/:id" element={<ClientProjectDetail />} />
                  <Route path="quotes" element={<AdminQuotes />} />
                  <Route path="quotes/new" element={<QuoteWizardPage />} />
                  <Route path="quotes/:id/edit" element={<QuoteWizardPage />} />
                  <Route path="quotes/:id" element={<AdminQuoteDetail />} />
                  <Route path="invoices" element={<AdminInvoices />} />
                  <Route path="payments" element={<Navigate to="/os/admin/invoices" replace />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="team" element={<AdminTeam />} />
                  <Route path="studio" element={<AdminStudio />} />
                  <Route path="resources" element={<AdminResources />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="monitoring" element={<AdminMonitoring />} />
                  <Route path="account" element={<Account />} />
                </Route>

                <Route path="/os/operator" element={<RequireRole roles={["operator","admin"]}><OSLayout role="operator" /></RequireRole>}>
                  <Route index element={<OperatorDashboard />} />
                  <Route path="projects" element={<OperatorProjects />} />
                  <Route path="projects/:id" element={<ClientProjectDetail />} />
                  <Route path="work-studio" element={<WorkStudio />} />
                  <Route path="account" element={<Account />} />
                </Route>

                <Route path="/os/client" element={<RequireRole roles={["client","admin"]}><OSLayout role="client" /></RequireRole>}>
                  <Route index element={<ClientDashboard />} />
                  <Route path="projects" element={<ClientProjects />} />
                  <Route path="projects/:id" element={<ClientProjectDetail />} />
                  <Route path="invoices" element={<ClientInvoices />} />
                  <Route path="quotes" element={<ClientQuotes />} />
                  <Route path="quotes/:id" element={<AdminQuoteDetail />} />
                  <Route path="account" element={<Account />} />
                </Route>

                {/* Guests Hub — public website */}
                <Route element={<Layout><Outlet /></Layout>}>
                  <Route path="/" element={<Index />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/portfolio" element={<Navigate to="/#clients" replace />} />
                  <Route path="/technology" element={<Technology />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/ventures" element={<Ventures />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
              </Suspense>
            </TooltipProvider>
            </ImpersonationProvider>
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;

function RouteFallback() {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background text-foreground">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
      <span className="sr-only">Loading…</span>
    </div>
  );
}
